import { Box } from '@mui/material';
import { useSharedResourceDownload } from 'components/common/withSharedResourceDownload/withSharedResourceDownload';
import { Spinner } from 'components/core';
import InsufficientPermissionsPage from 'components/core/InsufficientPermissionsPage/InsufficientPermissionsPage';
import qs from 'query-string';
import React, { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useResourceDownloadRouteParams } from 'hooks/navigation/useRouteParams';
import {
  BAD_REQUEST,
  CONFLICT,
  FORBIDDEN,
  NOT_FOUND,
  OK,
  REQUIRED_RESOURCE_NEEDS_AUTHORIZATION,
  SERVICE_UNAVAILABLE,
  TOKEN_EXPIRED,
  UNAUTHORIZED,
  UNPROCESSABLE_ENTITY,
} from 'shared/contants';
import {
  DownloadFailedInfo,
  DownloadingInfo,
  LinkExpiredInfo,
  NoCurrentVersionInfo,
  NotFoundInfo,
} from 'views/reports/statusComponents';
import {
  isNoCurrentVersionRefusal,
  type SharedResourceSignedRequestResult,
} from 'shared/download/sharedResourceSignedRequest';

const statusCodeComponents: Record<number, React.FC> = {
  [BAD_REQUEST]: DownloadFailedInfo,
  // Public link endpoints answer 401 when the link token fails verification.
  [UNAUTHORIZED]: LinkExpiredInfo,
  [FORBIDDEN]: InsufficientPermissionsPage,
  [NOT_FOUND]: NotFoundInfo,
  [CONFLICT]: NotFoundInfo,
  [UNPROCESSABLE_ENTITY]: DownloadFailedInfo,
  [TOKEN_EXPIRED]: LinkExpiredInfo,
  [REQUIRED_RESOURCE_NEEDS_AUTHORIZATION]: LinkExpiredInfo,
  [SERVICE_UNAVAILABLE]: DownloadFailedInfo,
  [OK]: DownloadingInfo,
}; //TODO https://hustro.atlassian.net/browse/PT-3862

/** What the server answered a public link with: its status and, for some refusals, why (PT-7117). */
export type DownloadOutcome = Pick<SharedResourceSignedRequestResult, 'statusCode' | 'refusal'>;

// Any resolved error status must end on a screen, never on an endless spinner.
export function getStatusComponent({ statusCode, refusal }: DownloadOutcome): React.FC | undefined {
  if (isNoCurrentVersionRefusal(refusal)) {
    return NoCurrentVersionInfo;
  }
  return (
    statusCodeComponents[statusCode] ??
    (statusCode >= 400 ? DownloadFailedInfo : undefined)
  );
}

/**
 * Cache of the last displayable outcome per download link: its status with the
 * reason of a refusal, so the screen it picks stays the same.
 * Preserves the resolved screen across component remounts (e.g. after auth
 * state changes) so the UI does not flicker back to a spinner.
 */
const lastDisplayableOutcomeByDownloadKey = new Map<string, DownloadOutcome>();

export function DownloadWithToken(): React.ReactElement {
  const {
    isDownloading,
    statusCode,
    refusal,
    startDownloadSharedResource: startDownloadingEntity,
  } = useSharedResourceDownload();

  const location = useLocation();

  const {
    reportId,
    documentId,
    issueId,
    eventId,
    incomingEmailMessageId,
    linkedDocumentationId,
  } = useResourceDownloadRouteParams();
  const { token } = qs.parse(location.search);

  const resourceId = reportId || documentId || linkedDocumentationId;
  const hasToken = typeof token === 'string';
  const downloadKey =
    resourceId && hasToken ? `${resourceId}:${token}` : undefined;

  const initiateDownload = useCallback(() => {
    if (typeof token !== 'string') {
      return;
    }

    startDownloadingEntity({
      reportId,
      documentId,
      issueId,
      eventId,
      incomingEmailMessageId,
      linkedDocumentationId,
      token,
    });
  }, [
    startDownloadingEntity,
    reportId,
    documentId,
    issueId,
    eventId,
    incomingEmailMessageId,
    linkedDocumentationId,
    token,
  ]);

  useEffect(() => {
    if (!resourceId || !hasToken) {
      return;
    }

    initiateDownload();
  }, [initiateDownload, resourceId, hasToken]);

  const currentOutcome: DownloadOutcome | undefined =
    statusCode !== undefined ? { statusCode, refusal } : undefined;
  const currentDisplayableOutcome =
    currentOutcome && getStatusComponent(currentOutcome) ? currentOutcome : undefined;

  if (downloadKey && currentDisplayableOutcome) {
    lastDisplayableOutcomeByDownloadKey.set(downloadKey, currentDisplayableOutcome);
  }

  const cachedOutcome = downloadKey
    ? lastDisplayableOutcomeByDownloadKey.get(downloadKey)
    : undefined;

  // While a new download is in progress (statusCode === undefined),
  // keep showing the previously resolved screen to avoid spinner flicker.
  const displayableOutcome =
    currentDisplayableOutcome ??
    (statusCode === undefined ? cachedOutcome : undefined);
  const DisplayedComponent = displayableOutcome
    ? getStatusComponent(displayableOutcome)
    : undefined;

  if (!DisplayedComponent) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100%',
        }}
      >
        <Spinner
          position='static'
          reason={`EntityDownload isDownloading: ${isDownloading}`}
          withServiceWorkerDiagnostics={false}
        />
      </Box>
    );
  }

  return <DisplayedComponent />;
}
